import { useEffect, useMemo, useState } from 'react';
import { formatCountPar } from '../lib/formatCountPar';
import type { ScanResultEntry } from '../types';

interface ResultsSheetProps {
  results: ScanResultEntry[];
  onForget: (idempotencyKey: string) => void;
  onTruth: (idempotencyKey: string, truth: number) => void;
}

export function ResultsSheet({ results, onForget, onTruth }: ResultsSheetProps) {
  const [zoomedKey, setZoomedKey] = useState<string | null>(null);
  const zoomed = zoomedKey
    ? results.find((r) => r.idempotencyKey === zoomedKey) ?? null
    : null;

  return (
    <section
      className="space-y-2 rounded-lg bg-gray-900 p-3 ring-1 ring-gray-800"
      data-testid="results-sheet"
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-gray-200">
          Decoded ({results.length})
        </p>
      </div>

      {results.length === 0 && (
        <p className="text-xs text-gray-500">Point at a bin label to start.</p>
      )}

      <ul className="space-y-2">
        {results.map((r) => (
          <ResultRow
            key={r.idempotencyKey}
            entry={r}
            onForget={onForget}
            onTruth={onTruth}
            onZoom={() => setZoomedKey(r.idempotencyKey)}
          />
        ))}
      </ul>

      {zoomed && (
        <SnapshotLightbox entry={zoomed} onClose={() => setZoomedKey(null)} />
      )}
    </section>
  );
}

function ResultRow({
  entry,
  onForget,
  onTruth,
  onZoom,
}: {
  entry: ScanResultEntry;
  onForget: (key: string) => void;
  onTruth: (key: string, truth: number) => void;
  onZoom: () => void;
}) {
  const thumbUrl = useObjectUrl(entry.snapshotBlob);
  const best = pickBestResult(entry);

  return (
    <li
      className="flex gap-3 rounded-md bg-gray-800/60 p-2 ring-1 ring-gray-700"
      data-testid={`result-row-${entry.qrValue}`}
    >
      {thumbUrl ? (
        <button
          type="button"
          onClick={onZoom}
          className="h-16 w-20 shrink-0 overflow-hidden rounded ring-1 ring-transparent transition hover:ring-emerald-400 focus:outline-none focus:ring-emerald-400"
          aria-label={`Open snapshot for ${entry.qrValue} full screen`}
          data-testid={`snapshot-zoom-${entry.qrValue}`}
        >
          <img
            src={thumbUrl}
            alt={`Snapshot for ${entry.qrValue}`}
            className="h-full w-full object-cover"
          />
        </button>
      ) : (
        <div className="h-16 w-20 shrink-0 rounded bg-gray-700" aria-hidden="true" />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <code className="truncate text-xs text-gray-300">{entry.qrValue}</code>
          <StatusPill entry={entry} />
        </div>

        <p className="mt-1 text-xs text-gray-400">
          {entry.response?.item.name ?? '—'}
        </p>

        {best && (
          <p className="mt-1 text-sm font-semibold text-white">
            Level {best.fullness ?? '—'}/5
            {best.confidence != null && (
              <span className="ml-1 text-xs text-gray-400">
                ({Math.round(best.confidence * 100)}%)
              </span>
            )}
            {best.count != null && (
              <span
                className="ml-2 text-xs font-normal text-gray-300"
                data-testid={`count-${entry.qrValue}`}
              >
                {formatCountPar(best.count, entry.response?.item.par_level ?? null)}
              </span>
            )}
          </p>
        )}

        {entry.errorMessage && (
          <p className="mt-1 text-xs text-red-300">{entry.errorMessage}</p>
        )}

        {entry.status === 'ok' && (
          <div className="mt-2 flex items-center gap-1">
            {[0, 1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => onTruth(entry.idempotencyKey, n)}
                className={[
                  'h-6 w-6 rounded text-xs',
                  entry.operatorTruth === n
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600',
                ].join(' ')}
                aria-label={`Set ground truth to ${n}`}
                data-testid={`truth-${entry.qrValue}-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => onForget(entry.idempotencyKey)}
        className="self-start rounded bg-gray-700 px-2 py-1 text-[10px] text-gray-300 hover:bg-gray-600"
        data-testid={`forget-${entry.qrValue}`}
        aria-label={`Re-scan ${entry.qrValue}`}
      >
        Re-scan
      </button>
    </li>
  );
}

function StatusPill({ entry }: { entry: ScanResultEntry }) {
  const palette =
    entry.status === 'ok' ? 'bg-emerald-500/20 text-emerald-200'
    : entry.status === 'pending' ? 'bg-blue-500/20 text-blue-200'
    : entry.status === 'aborted' ? 'bg-gray-500/20 text-gray-300'
    : 'bg-red-500/20 text-red-200';
  return (
    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] ${palette}`}>
      {entry.status}
    </span>
  );
}

function SnapshotLightbox({
  entry,
  onClose,
}: {
  entry: ScanResultEntry;
  onClose: () => void;
}) {
  const url = useObjectUrl(entry.snapshotBlob);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Snapshot for ${entry.qrValue}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
      data-testid="snapshot-lightbox"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute right-4 top-4 rounded-full bg-gray-800/80 px-3 py-1 text-sm text-gray-200 ring-1 ring-gray-600 hover:bg-gray-700"
        aria-label="Close snapshot"
        data-testid="snapshot-lightbox-close"
      >
        Close
      </button>

      <div
        className="flex max-h-full max-w-full flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        {url ? (
          <img
            src={url}
            alt={`Snapshot for ${entry.qrValue}`}
            className="max-h-[85vh] max-w-[95vw] rounded object-contain"
          />
        ) : (
          <p className="text-sm text-gray-300">Snapshot unavailable.</p>
        )}
        <code className="rounded bg-gray-900/80 px-2 py-1 text-xs text-gray-200 ring-1 ring-gray-700">
          {entry.qrValue}
        </code>
      </div>
    </div>
  );
}

function pickBestResult(entry: ScanResultEntry) {
  if (!entry.response) return null;
  const ok = entry.response.results.filter((r) => r.status === 'ok' && r.fullness != null);
  if (ok.length === 0) return null;
  return ok.reduce((best, r) =>
    (r.confidence ?? 0) > (best.confidence ?? 0) ? r : best
  );
}

/**
 * Lazy object-URL hook: generates the URL inside the effect so it lives
 * tied to the mount and is revoked in cleanup. Without this, hour-long
 * sweeps with hundreds of results leak hundreds of MB and Safari kills
 * the tab.
 */
function useObjectUrl(blob: Blob): string | null {
  // Derive the URL during render (cheap) and revoke it in cleanup. This
  // avoids the setState-in-effect rule while keeping the lifetime tied
  // to the mount so hour-long sweeps don't leak hundreds of MB.
  //
  // In React StrictMode dev, the effect cleanup runs after a sham unmount,
  // revoking the URL while the <img> still has the src in the next mount.
  // Browser tolerates it (the cached blob URL resolves anyway) — you may
  // see a one-frame broken thumbnail in dev. Production is unaffected.
  // Don't "fix" by switching to a setState-in-effect pattern: that
  // re-introduces the lint violation we deliberately moved past.
  const url = useMemo(() => {
    if (!blob || blob.size === 0) return null;
    return URL.createObjectURL(blob);
  }, [blob]);
  useEffect(() => {
    if (!url) return;
    return () => URL.revokeObjectURL(url);
  }, [url]);
  return url;
}
